import React, { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import {
  fetchAccessScopeCatalog,
  fetchAccessScopes,
  resetAccessScope,
  saveAccessScope,
  setAccessScopeStatus,
} from "./iamApi";
import { displayRoleName, initials, roleTone } from "./iamStudio";

function Badge({ children, tone }) {
  return <span className={`iam-studio-badge ${tone}`}>{children}</span>;
}

const EMPTY_FORM = {
  allLocations: false,
  locationIds: [],
  allDepartments: false,
  departmentIds: [],
  employeeScope: "all_employees",
};

export default function IamAccessScopeStudio({ initialUserId, onConsumedInitial, onNotify }) {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState({ locations: [], departments: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(initialUserId || null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [scopeFilter, setScopeFilter] = useState("all");

  const load = async (term = search) => {
    setLoading(true);
    try {
      const [list, cat] = await Promise.all([
        fetchAccessScopes(term || undefined),
        fetchAccessScopeCatalog(),
      ]);
      setRows(list || []);
      setCatalog(cat || { locations: [], departments: [] });
    } catch (err) {
      onNotify?.(err?.response?.data?.detail || "Could not load access scope", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("");
    // initial catalog + directory
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialUserId) {
      setSelectedId(initialUserId);
      setEditing(true);
      onConsumedInitial?.();
    }
  }, [initialUserId, onConsumedInitial]);

  const selected = rows.find((row) => row.user_id === selectedId) || null;
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (scopeFilter === "configured" && row.scope_status === "unconfigured") return false;
      if (scopeFilter === "missing" && row.scope_status !== "unconfigured") return false;
      if (!query) return true;
      const roleName = displayRoleName((row.roles || [])[0] || row.role || "");
      return [row.full_name, row.email, row.location_summary, row.department_summary, roleName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [rows, search, scopeFilter]);
  const configuredCount = rows.filter((row) => row.scope_status !== "unconfigured").length;

  useEffect(() => {
    if (!selected) return;
    setForm({
      allLocations: !!selected.all_locations,
      locationIds: selected.location_ids || [],
      allDepartments: !!selected.all_departments,
      departmentIds: selected.department_ids || [],
      employeeScope: selected.employee_scope || "all_employees",
    });
    setError("");
  }, [selected]);

  const groupedDepartments = useMemo(() => {
    const departments = catalog.departments || [];
    const locations = form.allLocations
      ? catalog.locations || []
      : (catalog.locations || []).filter((loc) => form.locationIds.includes(loc.location_id));
    const groups = locations.map((loc) => ({
      id: loc.location_id,
      name: loc.name,
      departments: departments.filter((dept) => (dept.location_ids || []).includes(loc.location_id)),
    }));
    const shared = departments.filter((dept) => dept.shared);
    return { groups, shared };
  }, [catalog, form.allLocations, form.locationIds]);

  const toggleId = (key, id) => {
    setForm((current) => {
      const has = current[key].includes(id);
      const next = { ...current, [key]: has ? current[key].filter((item) => item !== id) : [...current[key], id] };
      if (key === "locationIds") {
        const allowed = new Set(
          (catalog.departments || [])
            .filter((dept) => dept.shared || (dept.location_ids || []).some((loc) => next.locationIds.includes(loc)))
            .map((dept) => dept.department_id)
        );
        next.departmentIds = next.departmentIds.filter((deptId) => allowed.has(deptId));
      }
      return next;
    });
  };

  const save = async () => {
    if (!selected) return;
    if (!form.allLocations && !form.locationIds.length) {
      setError("Select at least one location, or choose All Locations.");
      return;
    }
    if (!form.allDepartments && !form.departmentIds.length) {
      setError("Select at least one department, or choose All Departments.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await saveAccessScope(selected.user_id, {
        all_locations: form.allLocations,
        location_ids: form.allLocations ? [] : form.locationIds,
        all_departments: form.allDepartments,
        department_ids: form.allDepartments ? [] : form.departmentIds,
        employee_scope: form.employeeScope,
      });
      setRows((current) => current.map((row) => (row.user_id === saved.user_id ? saved : row)));
      setEditing(false);
      onNotify?.("Access scope saved", "success");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save access scope");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (isActive) => {
    if (!selected) return;
    setSaving(true);
    try {
      const saved = await setAccessScopeStatus(selected.user_id, isActive);
      setRows((current) => current.map((row) => (row.user_id === saved.user_id ? saved : row)));
      onNotify?.(isActive ? "Access scope activated" : "Access scope deactivated", "success");
    } catch (err) {
      onNotify?.(err?.response?.data?.detail || "Could not update scope status", "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmReset = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const saved = await resetAccessScope(selected.user_id);
      setRows((current) => current.map((row) => (row.user_id === saved.user_id ? saved : row)));
      setEditing(false);
      setConfirm(null);
      onNotify?.("Access scope cleared", "success");
    } catch (err) {
      onNotify?.(err?.response?.data?.detail || "Could not reset access scope", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`iam-ux ${selected ? "with-drawer" : ""}`}>
      <div className="iam-ux-main">
        <header className="iam-ux-head">
          <div>
            <h2>Access Scope</h2>
            <p>Choose which locations, departments, and people each login can see. A role still decides what they can do.</p>
          </div>
        </header>
        <div className="iam-scope-stats">
          <button type="button" className={scopeFilter === "all" ? "on" : ""} onClick={() => setScopeFilter("all")}>
            <strong>{rows.length}</strong>
            <span>All users</span>
          </button>
          <button type="button" className={scopeFilter === "configured" ? "on" : ""} onClick={() => setScopeFilter("configured")}>
            <strong>{configuredCount}</strong>
            <span>Scope set</span>
          </button>
          <button type="button" className={scopeFilter === "missing" ? "on warn" : ""} onClick={() => setScopeFilter("missing")}>
            <strong>{rows.length - configuredCount}</strong>
            <span>Not configured</span>
          </button>
        </div>
        <div className="iam-ux-toolbar">
          <label className="iam-ux-search">
            <Search size={16} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, location, or role"
            />
          </label>
        </div>
        <div className="iam-ux-card">
          {loading ? <p className="iam-ux-empty">Loading access scope…</p> : (
            <table className="iam-ux-table iam-scope-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Account</th>
                  <th>Locations</th>
                  <th>Departments</th>
                  <th>People they see</th>
                  <th>Scope</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const roleName = displayRoleName((row.roles || [])[0] || row.role || "—");
                  const missing = row.scope_status === "unconfigured";
                  return (
                    <tr
                      key={row.user_id}
                      className={selectedId === row.user_id ? "selected" : ""}
                      onClick={() => {
                        setSelectedId(row.user_id);
                        setEditing(missing);
                      }}
                    >
                      <td>
                        <div className="iam-studio-person">
                          <span className={`iam-studio-avatar ${roleTone(roleName)}`}>{initials(row.full_name)}</span>
                          <span>
                            <strong>{row.full_name || row.email}</strong>
                            <small>{row.email}</small>
                          </span>
                        </div>
                      </td>
                      <td><Badge tone={roleTone(roleName)}>{roleName}</Badge></td>
                      <td>
                        <span className={`iam-ux-status ${row.status === "active" ? "on" : "off"}`}>
                          <i />{row.status === "active" ? "Active" : (row.status || "—")}
                        </span>
                      </td>
                      <td><span className="iam-scope-chip">{missing ? "Not set" : (row.location_summary || "—")}</span></td>
                      <td><span className="iam-scope-chip">{missing ? "Not set" : (row.department_summary || "—")}</span></td>
                      <td>{missing ? "—" : (row.employee_scope_label || "—")}</td>
                      <td>
                        <span className={`iam-ux-status ${row.scope_status === "active" ? "on" : row.scope_status === "inactive" ? "off" : "off"}`}>
                          <i />
                          {row.scope_status === "active" ? "Active" : row.scope_status === "inactive" ? "Inactive" : "Not configured"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading && !rows.length && (
            <div className="iam-ux-zero">
              <strong>No users to scope</strong>
              <p>Create a login on the Users page first. Then come back here to limit which locations and departments they can see.</p>
            </div>
          )}
          {!loading && rows.length > 0 && !visible.length && (
            <p className="iam-ux-empty">No users match this search.</p>
          )}
        </div>
      </div>

      {selected && <button type="button" className="iam-ux-backdrop" aria-label="Close panel" onClick={() => { setSelectedId(null); setEditing(false); }} />}

      {selected && (
        <aside className="iam-ux-drawer large">
          <header>
            <div>
              <h3>{selected.full_name || "User"}</h3>
              <p>{displayRoleName((selected.roles || [])[0] || selected.role || "No role")} · {selected.status === "active" ? "Active" : (selected.status || "—")}</p>
            </div>
            <button type="button" className="iam-studio-icon" onClick={() => { setSelectedId(null); setEditing(false); }} aria-label="Close">
              <X size={16} />
            </button>
          </header>
          <div className="iam-ux-drawer-body">
            <div className="iam-scope-now">
              <div>
                <span>Locations</span>
                <strong>{selected.scope_status === "unconfigured" ? "Not set" : (selected.location_summary || "—")}</strong>
              </div>
              <div>
                <span>Departments</span>
                <strong>{selected.scope_status === "unconfigured" ? "Not set" : (selected.department_summary || "—")}</strong>
              </div>
              <div>
                <span>People</span>
                <strong>{selected.scope_status === "unconfigured" ? "Not set" : (selected.employee_scope_label || "—")}</strong>
              </div>
            </div>
            {selected.summary && <p className="iam-ux-note">{selected.summary}</p>}
            {(selected.warnings || []).map((warning) => <p key={warning} className="iam-studio-error">{warning}</p>)}
            {!editing ? null : (
              <>
                <div className="iam-studio-field">
                  <span>Locations</span>
                  <div className="iam-studio-inline-actions">
                    <button type="button" className={`iam-studio-btn ${form.allLocations ? "primary" : ""}`} onClick={() => setForm((f) => ({ ...f, allLocations: true, locationIds: [] }))}>All locations</button>
                    <button type="button" className={`iam-studio-btn ${!form.allLocations ? "primary" : ""}`} onClick={() => setForm((f) => ({ ...f, allLocations: false }))}>Selected locations</button>
                    {!form.allLocations && (
                      <button type="button" className="iam-studio-btn ghost" onClick={() => setForm((f) => ({ ...f, locationIds: (catalog.locations || []).map((l) => l.location_id) }))}>
                        Select all
                      </button>
                    )}
                  </div>
                  {form.allLocations ? <p className="iam-studio-note">All locations in this organisation.</p> : (
                    <div className="iam-studio-chips">
                      {(catalog.locations || []).map((loc) => {
                        const on = form.locationIds.includes(loc.location_id);
                        return (
                          <button
                            key={loc.location_id}
                            type="button"
                            className={`iam-studio-chip ${on ? "on" : ""}`}
                            onClick={() => toggleId("locationIds", loc.location_id)}
                          >
                            {loc.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="iam-studio-field">
                  <span>Departments</span>
                  <label className="iam-studio-checkline">
                    <input
                      type="checkbox"
                      checked={form.allDepartments}
                      onChange={(e) => setForm((f) => ({ ...f, allDepartments: e.target.checked, departmentIds: e.target.checked ? [] : f.departmentIds }))}
                    />
                    All departments in the selected locations
                  </label>
                  {!form.allDepartments && (
                    <>
                      {groupedDepartments.groups.map((group) => (
                        <div key={group.id} className="iam-studio-dept-group">
                          <strong>{group.name}</strong>
                          {group.departments.length ? group.departments.map((dept) => (
                            <label key={`${group.id}-${dept.department_id}`}>
                              <input
                                type="checkbox"
                                checked={form.departmentIds.includes(dept.department_id)}
                                onChange={() => toggleId("departmentIds", dept.department_id)}
                              />
                              {dept.name}
                            </label>
                          )) : <small>No departments are recorded at this location yet.</small>}
                        </div>
                      ))}
                      {!!groupedDepartments.shared.length && (
                        <div className="iam-studio-dept-group">
                          <strong>Shared departments</strong>
                          {groupedDepartments.shared.map((dept) => (
                            <label key={dept.department_id}>
                              <input
                                type="checkbox"
                                checked={form.departmentIds.includes(dept.department_id)}
                                onChange={() => toggleId("departmentIds", dept.department_id)}
                              />
                              {dept.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="iam-studio-field">
                  <span>Employee scope</span>
                  <label className="iam-studio-checkline">
                    <input type="radio" name="employeeScope" checked={form.employeeScope === "all_employees"} onChange={() => setForm((f) => ({ ...f, employeeScope: "all_employees" }))} />
                    All employees within selected scope
                  </label>
                  <label className="iam-studio-checkline">
                    <input type="radio" name="employeeScope" checked={form.employeeScope === "reporting_employees"} onChange={() => setForm((f) => ({ ...f, employeeScope: "reporting_employees" }))} />
                    Reporting employees only
                  </label>
                </div>
                {error && <p className="iam-studio-error">{error}</p>}
              </>
            )}
          </div>
          <footer>
            {!editing ? (
              <button type="button" className="iam-studio-btn primary" onClick={() => setEditing(true)}>
                {selected.configured ? "Edit access scope" : "Configure access scope"}
              </button>
            ) : (
              <>
                <button type="button" className="iam-studio-btn" onClick={() => setEditing(false)}>Cancel</button>
                <button type="button" className="iam-studio-btn primary" disabled={saving} onClick={save}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </>
            )}
            {selected.configured && selected.is_active && (
              <button type="button" className="iam-studio-btn" disabled={saving} onClick={() => changeStatus(false)}>Deactivate</button>
            )}
            {selected.configured && !selected.is_active && (
              <button type="button" className="iam-studio-btn" disabled={saving} onClick={() => changeStatus(true)}>Activate</button>
            )}
            {selected.configured && (
              <button type="button" className="iam-studio-btn danger" disabled={saving} onClick={() => setConfirm(selected.full_name)}>
                Reset
              </button>
            )}
          </footer>
        </aside>
      )}

      {confirm && (
        <div className="iam-studio-confirm" role="dialog">
          <div>
            <h3>Clear access scope?</h3>
            <p>This removes the saved boundary for {confirm}. Their role permissions stay. Until a new scope is saved, organisation data follows the previous unrestricted behaviour.</p>
            <div>
              <button type="button" className="iam-studio-btn" onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button" className="iam-studio-btn danger" onClick={confirmReset}>Clear scope</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
